import {
  Children,
  ComponentProps,
  FC,
  ReactElement,
  ReactNode,
  cloneElement,
  createContext,
  isValidElement,
  useContext,
} from 'react';
import { twMerge } from 'tailwind-merge';
import { Typography } from '../../Typography';

export type LeagueCardSize = 'small' | 'large' | 'responsive';
export type LeagueCardSurface = 'default' | 'muted' | 'sponsor';
export type LeagueCardTrend = 'up' | 'down' | 'none';

type LeagueCardSlotName = 'media' | 'title' | 'badge' | 'meta' | 'action';

interface LeagueCardContextValue {
  size: LeagueCardSize;
  surface: LeagueCardSurface;
}

const LeagueCardContext = createContext<LeagueCardContextValue>({
  size: 'responsive',
  surface: 'default',
});

const useLeagueCardContext = () => useContext(LeagueCardContext);

const sizeClassMap = {
  mediaWidth: {
    small: 'w-[88px]',
    large: 'w-[160px]',
    responsive: 'w-[88px] md:w-[160px]',
  },
  contentPadding: {
    small: 'p-[16px]',
    large: 'p-[20px]',
    responsive: 'p-[16px] md:p-[20px]',
  },
  title: {
    small: 'text-20 leading-24',
    large: 'text-24 leading-28',
    responsive: 'text-20 leading-24 md:text-24 md:leading-28',
  },
  metaWrapGap: {
    small: 'gap-x-[16px] gap-y-[4px]',
    large: 'gap-x-[20px] gap-y-[4px]',
    responsive: 'gap-x-[16px] gap-y-[4px] md:gap-x-[20px]',
  },
  metaText: {
    small: 'text-12 leading-16',
    large: 'text-14 leading-20',
    responsive: 'text-12 leading-16 md:text-14 md:leading-20',
  },
} as const;

const surfaceClassMap: Record<LeagueCardSurface, string> = {
  default: 'bg-[white]',
  muted: 'bg-[#eae9fd]',
  sponsor: 'bg-[#242651]',
};

const headingColorMap: Record<LeagueCardSurface, string> = {
  default: 'text-[#222526]',
  muted: 'text-[#222526]',
  sponsor: 'text-[#2efcad]',
};

const actionColorMap: Record<LeagueCardSurface, string> = {
  default: 'text-[#0072ee]',
  muted: 'text-[#0072ee]',
  sponsor: 'text-[#2efcad]',
};

const metaColorMap: Record<LeagueCardSurface, string> = {
  default: 'text-[#222526]',
  muted: 'text-[#222526]',
  sponsor: 'text-[white]',
};

const dividerColorMap: Record<LeagueCardSurface, string> = {
  default: 'border-[#eae9fd]',
  muted: 'border-[#eae9fd]',
  sponsor: 'border-[#4c5094]',
};

const badgeColorMap: Record<LeagueCardSurface, string> = {
  default: 'bg-[#8ce28b] text-[#222526]',
  muted: 'bg-[#8ce28b] text-[#222526]',
  sponsor: 'bg-[#2efcad] text-[#242651]',
};

type SlotElement = ReactElement<{ className?: string }>;

interface LeagueCardProps extends ComponentProps<'article'> {
  size?: LeagueCardSize;
  surface?: LeagueCardSurface;
  children: ReactNode;
}

interface LeagueCardMediaProps extends ComponentProps<'div'> {
  mode?: 'fullImage' | 'logo' | 'color' | 'transparent';
  divider?: boolean;
  children?: ReactNode;
}

interface LeagueCardImageProps extends Omit<ComponentProps<'img'>, 'src' | 'alt'> {
  src: string;
  alt: string;
  fit?: 'cover' | 'contain';
}

interface LeagueCardTitleProps extends ComponentProps<'p'> {
  children: ReactNode;
}

interface LeagueCardBadgeProps extends ComponentProps<'span'> {
  children?: ReactNode;
}

interface LeagueCardMetaProps extends ComponentProps<'div'> {
  children: ReactNode;
}

interface LeagueCardMetaItemProps extends ComponentProps<'div'> {
  label: string;
  value: string | number;
  trend?: LeagueCardTrend;
}

interface LeagueCardActionProps extends ComponentProps<'button'> {
  children: ReactNode;
}

type SlotComponent<T> = FC<T> & {
  __leagueCardSlot: LeagueCardSlotName;
};

const getSlotName = (child: ReactNode): LeagueCardSlotName | undefined => {
  if (!isValidElement(child)) return undefined;
  return (child.type as SlotComponent<unknown>).__leagueCardSlot;
};

const cloneWithClassName = (
  slotElement: SlotElement | undefined,
  className: string,
  key: string
): SlotElement | null => {
  if (!slotElement) return null;
  return cloneElement(slotElement, {
    key,
    className: twMerge(slotElement.props.className, className),
  });
};

const Media: SlotComponent<LeagueCardMediaProps> = ({
  className,
  mode = 'logo',
  divider = true,
  children,
  ...otherProps
}) => {
  const { size, surface } = useLeagueCardContext();

  const modeClassName =
    mode === 'fullImage'
      ? 'items-stretch justify-stretch'
      : mode === 'transparent'
        ? 'items-center justify-center bg-transparent'
        : 'items-center justify-center';

  return (
    <div
      className={twMerge(
        'relative flex shrink-0 self-stretch overflow-hidden',
        sizeClassMap.mediaWidth[size],
        modeClassName,
        divider ? twMerge('border-r border-solid', dividerColorMap[surface]) : '',
        className
      )}
      {...otherProps}
    >
      {children}
    </div>
  );
};
Media.__leagueCardSlot = 'media';

const Image: FC<LeagueCardImageProps> = ({
  className,
  fit = 'contain',
  src,
  alt,
  ...otherProps
}) => (
  <img
    src={src}
    alt={alt}
    className={twMerge('size-full', fit === 'cover' ? 'object-cover' : 'object-contain', className)}
    {...otherProps}
  />
);

const Title: SlotComponent<LeagueCardTitleProps> = ({ className, children, ...otherProps }) => {
  const { size, surface } = useLeagueCardContext();

  return (
    <Typography
      as="h3"
      variant="headline025"
      className={twMerge(
        'w-full truncate',
        sizeClassMap.title[size],
        headingColorMap[surface],
        className
      )}
      {...otherProps}
    >
      {children}
    </Typography>
  );
};
Title.__leagueCardSlot = 'title';

const Badge: SlotComponent<LeagueCardBadgeProps> = ({
  className,
  children = 'Complete',
  ...otherProps
}) => {
  const { surface } = useLeagueCardContext();

  return (
    <span
      className={twMerge(
        'inline-flex items-center justify-center rounded-[4px] px-[8px] py-[2px]',
        badgeColorMap[surface],
        className
      )}
      {...otherProps}
    >
      <Typography as="span" variant="label020" className="font-bold">
        {children}
      </Typography>
    </span>
  );
};
Badge.__leagueCardSlot = 'badge';

const Meta: SlotComponent<LeagueCardMetaProps> = ({ className, children, ...otherProps }) => {
  const { size } = useLeagueCardContext();

  return (
    <div
      className={twMerge('flex flex-wrap items-center', sizeClassMap.metaWrapGap[size], className)}
      {...otherProps}
    >
      {children}
    </div>
  );
};
Meta.__leagueCardSlot = 'meta';

const trendColorMap: Record<Exclude<LeagueCardTrend, 'none'>, string> = {
  up: 'text-[#8ce28b]',
  down: 'text-[#de2a2f]',
};

const trendCharacterMap: Record<Exclude<LeagueCardTrend, 'none'>, string> = {
  up: '▲',
  down: '▼',
};

const MetaItem: FC<LeagueCardMetaItemProps> = ({
  className,
  label,
  value,
  trend = 'none',
  ...otherProps
}) => {
  const { size, surface } = useLeagueCardContext();
  const textColorClass = metaColorMap[surface];

  return (
    <div className={twMerge('inline-flex items-center gap-[4px]', className)} {...otherProps}>
      <Typography
        as="span"
        variant="paragraph010"
        className={twMerge(sizeClassMap.metaText[size], textColorClass)}
      >
        {label}
      </Typography>
      <Typography
        as="span"
        variant="subheadline010"
        className={twMerge(sizeClassMap.metaText[size], textColorClass)}
      >
        {value}
      </Typography>
      {trend !== 'none' && (
        <span className={twMerge('text-[8px] leading-none', trendColorMap[trend])}>
          {trendCharacterMap[trend]}
        </span>
      )}
    </div>
  );
};

const Action: SlotComponent<LeagueCardActionProps> = ({
  className,
  children,
  type = 'button',
  ...otherProps
}) => {
  const { surface } = useLeagueCardContext();

  return (
    <button
      type={type}
      className={twMerge(
        'inline-flex items-center rounded-[4px] px-0 py-[3px]',
        actionColorMap[surface],
        className
      )}
      {...otherProps}
    >
      <Typography as="span" variant="button020" className={actionColorMap[surface]}>
        {children}
      </Typography>
    </button>
  );
};
Action.__leagueCardSlot = 'action';

type LeagueCardComponent = FC<LeagueCardProps> & {
  Media: typeof Media;
  Image: typeof Image;
  Title: typeof Title;
  Badge: typeof Badge;
  Meta: typeof Meta;
  MetaItem: typeof MetaItem;
  Action: typeof Action;
};

export const LeagueCard: LeagueCardComponent = ({
  className,
  size = 'responsive',
  surface = 'default',
  children,
  ...otherProps
}) => {
  const slots: Partial<Record<LeagueCardSlotName, SlotElement>> = {};

  Children.forEach(children, child => {
    const slotName = getSlotName(child);
    if (!slotName || !isValidElement(child) || slots[slotName]) return;
    slots[slotName] = child as SlotElement;
  });

  const shouldShowInlineAction = Boolean(slots.action && size !== 'small');
  const shouldShowBottomAction = Boolean(slots.action && size !== 'large');
  const shouldShowHeaderBadge = Boolean(slots.badge && size !== 'small');
  const shouldShowBottomBadge = Boolean(slots.badge && size !== 'large');

  const inlineAction = shouldShowInlineAction
    ? cloneWithClassName(
        slots.action,
        size === 'responsive' ? 'hidden md:inline-flex md:shrink-0' : 'shrink-0',
        'inline-action'
      )
    : null;

  const bottomAction = shouldShowBottomAction
    ? cloneWithClassName(
        slots.action,
        size === 'responsive' ? 'inline-flex md:hidden' : 'inline-flex',
        'bottom-action'
      )
    : null;

  const headerBadge = shouldShowHeaderBadge
    ? cloneWithClassName(
        slots.badge,
        size === 'responsive' ? 'hidden md:inline-flex md:shrink-0' : 'shrink-0',
        'header-badge'
      )
    : null;

  const bottomBadge = shouldShowBottomBadge
    ? cloneWithClassName(
        slots.badge,
        size === 'responsive' ? 'inline-flex md:hidden' : 'inline-flex',
        'bottom-badge'
      )
    : null;

  return (
    <LeagueCardContext.Provider value={{ size, surface }}>
      <article
        className={twMerge(
          'flex items-stretch overflow-hidden rounded-[12px]',
          surfaceClassMap[surface],
          className
        )}
        {...otherProps}
      >
        {slots.media}
        <div
          className={twMerge(
            'flex min-w-0 flex-1 flex-col justify-center gap-[12px]',
            sizeClassMap.contentPadding[size]
          )}
        >
          {(slots.title || headerBadge) && (
            <div className="flex w-full items-start gap-[12px]">
              <div className="min-w-0 flex-1">{slots.title}</div>
              {headerBadge}
            </div>
          )}

          {(slots.meta || inlineAction) && (
            <div className="flex w-full items-end gap-[12px]">
              <div className="min-w-0 flex-1">{slots.meta}</div>
              {inlineAction}
            </div>
          )}

          {(bottomAction || bottomBadge) && (
            <div className="flex w-full items-center gap-[12px]">
              {bottomAction}
              {bottomBadge}
            </div>
          )}
        </div>
      </article>
    </LeagueCardContext.Provider>
  );
};

LeagueCard.Media = Media;
LeagueCard.Image = Image;
LeagueCard.Title = Title;
LeagueCard.Badge = Badge;
LeagueCard.Meta = Meta;
LeagueCard.MetaItem = MetaItem;
LeagueCard.Action = Action;
